import { useNavigate } from "react-router-dom";
import {
  debounce,
  Divider,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Switch,
  TablePagination,
} from "@mui/material";
import { useState, SyntheticEvent, ChangeEvent, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import usePopup from "../../../hooks/usePopup";
import moduleRoutes from "../../../routes/moduleRouteList";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import mastersRouteList from "../../routeList";
import generalMasterRouteList from "../routeList";
import { SearchParamsType, MasterTableDataType, MasterDataType } from "./types";
import { getMastersTableData, saveMasterData } from "./services";
import EditIcon from "@mui/icons-material/Edit";
import useAuthentication from "../../../hooks/useAuthentication";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
const pageTitle = "Speciality ";
const base = (window as any)._env_?.BASE_URL;
const SpecialityMaster = () => {
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    pageNumber: 0,
    pageSize: 25,
    sortField: "",
    sortOrder: "",
    filter: {
      searchText: "",
      status: 2,
    },
  });

  const [open, setOpen] = useState<boolean>(false);
  const { CustomModal, showSnackbar } = usePopup();
  const [changedStatus, setChangedStatus] = useState<number>(2);
  const [selected, setSelected] = useState<{
    [key: number]: MasterTableDataType;
  }>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const {
    data: tableData,
    isFetching: tableDataLoading,
    refetch,
  } = useQuery(
    [`${pageTitle}masterlist`, searchParams],
    async () => getMastersTableData(searchParams),
    {
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  const { mutate: updateStatus, isLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        refetch();
        setSelected({});
        setOpen(false);
        setChangedStatus(2);
        setSelectedId(null);
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    pageNumber: number
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleStatusChange = (
    _: SyntheticEvent,
    value: { label: string; value: number } | null
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      filter: { ...prev.filter, status: value ? value.value : 3 },
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchParams((prev) => ({
      ...prev,
      filter: { ...prev.filter, searchText: e.target.value },
    }));
  };

  const debounceSearch = debounce(handleSearchChange, 500);

  const handleNavigateClick = (data?: MasterTableDataType) => {
    navigate(
      `${base}${moduleRoutes.mainRoute}/${moduleRoutes.masters}/${mastersRouteList.generalMaster}/${generalMasterRouteList.specialityMaster}/${generalMasterRouteList.addOrEdit}`,
      { state: { data: data || null } }
    );
  };

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const objs: { [key: number]: MasterTableDataType } = {};
      tableData?.response?.items?.map(
        (item: MasterTableDataType, index: number) => {
          objs[index] = item as MasterTableDataType;
        }
      );
      setSelected(objs);
    } else {
      setSelected({});
    }
  };

  const handleSelect = (
    isChecked: boolean,
    item: MasterTableDataType,
    index: number
  ) => {
    if (isChecked) {
      setSelected((prev) => ({
        ...prev,
        [index]: item,
      }));
    } else {
      setSelected((prev) => {
        const newSelected = { ...prev };
        delete newSelected[index];
        return newSelected;
      });
    }
  };
  const handleOpenChangeStatus = () => {
    setOpen(true);
  };

  const handleCloseChangeStatus = () => {
    setOpen(false);
  };

  const handleUpdateStatus = () => {
    if (changedStatus == 2) {
      showSnackbar({
        message: "Please select a status !",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    } else {
      const items: MasterDataType[] = [];
      for (let key in selected) {
        items.push({
          deptId: selected[key].deptId,
          deptStatus: status ? 1 : 0,
          deptName: selected[key].deptName,
          deptCode: selected[key].deptCode,
          clinicId: Number(user?.clinicId),
          createdById: Number(user?.userId),
          createdByDate: "",
          externalRef: selected[key].isExternalRef,
          refRequired: selected[key].isRefRequired,
          skipVitals: selected[key].skipVitals,
        });
      }
      updateStatus(items);
    }
  };

  const handleUpdateOneStatus = (
    status: boolean,
    data: MasterTableDataType
  ) => {
    setSelectedId(data.deptId);
    updateStatus([
      {
        deptId: data.deptId,
        deptStatus: status ? 1 : 0,
        deptName: data.deptName,
        deptCode: data.deptCode,
        clinicId: Number(user?.clinicId),
        createdById: Number(user?.userId),
        createdByDate: "",
        externalRef: data.isExternalRef,
        refRequired: data.isRefRequired,
        skipVitals: data.skipVitals,
      },
    ]);
  };

  const selectedSize = useMemo(() => {
    return Object.keys(selected).length;
  }, [selected]);
  return (
    <div className="p-3">
      <CustomModal
        open={open || (isLoading && Boolean(selectedSize))}
        setOpen={setOpen}
        title={`Change status`}
        handleClose={handleCloseChangeStatus}
      >
        <div className="w-[50vw]">
          <div className="max-h-[65vh] overflow-y-auto">
            {Object.entries(selected).map(([key, item], index) => (
              <div
                key={`${index + key}`}
                className="flex gap-2 ps-3 justify-start items-center py-2 overflow-y-auto"
              >
                <div className="h-[7px] w-[7px] bg-green-600 rounded m-2"></div>{" "}
                <div>{item?.deptName}</div>
              </div>
            ))}
          </div>
          <Divider />
          <div className="flex justify-end items-center gap-2 p-3">
            <Autocomplete
              disableClearable
              sx={{ width: "200px" }}
              options={status}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => <TextField {...params} label="Status" />}
              onChange={(_, value) => {
                setChangedStatus(value?.value);
              }}
            />
            <Button
              disabled={isLoading}
              endIcon={
                isLoading && <CircularProgress size={20} color="success" />
              }
              color="success"
              onClick={handleUpdateStatus}
            >
              Update
            </Button>
          </div>
        </div>
      </CustomModal>

      <Paper>
        <div className="flex justify-between p-3">
          <div className="flex gap-2">
            <Typography variant="h6" color={"default"}>
              {pageTitle} Master
            </Typography>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                handleNavigateClick();
              }}
            >
              new {pageTitle}
            </Button>
          </div>
        </div>
        <Divider />
        <div className="">
          <div className="grid grid-cols-4 gap-3 py-3 px-2">
            <Autocomplete
              disableClearable
              fullWidth
              defaultValue={
                selectedStatus[
                  searchParams.filter.status as keyof typeof selectedStatus
                ]
              }
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => <TextField {...params} label="Status" />}
              onChange={handleStatusChange}
            />
            <TextField label={"Search"} onChange={debounceSearch} />
          </div>
          <TableContainer className="border">
            {Boolean(selectedSize) && (
              <div className="p-2">
                <strong>Selected items {selectedSize}</strong>{" "}
                <Button onClick={handleOpenChangeStatus}>Change Status</Button>
              </div>
            )}
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      checked={
                        selectedSize == tableData?.response?.items?.length &&
                        tableData?.response?.items?.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {headings.map((heading) => (
                    <TableCell key={heading.key} className="font-bold">
                      {heading.label}
                    </TableCell>
                  ))}
                  <TableCell className="font-bold">Skip vitals</TableCell>
                  <TableCell className="font-bold">External Ref</TableCell>
                  <TableCell className="font-bold">Ref required</TableCell>
                  <TableCell className="font-bold">Status</TableCell>
                  <TableCell className="font-bold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <CustomTableBody
                alert={"No data"}
                dataLoading={tableDataLoading}
                isData={Boolean(tableData?.response?.items?.length)}
                totalSpan={{
                  row: searchParams.pageSize,
                  col: headings.length + 6,
                }}
              >
                {tableData?.response?.items?.map((tableItem, index) => (
                  <TableRow key={`${pageTitle}-${index}`}>
                    <TableCell>
                      <Checkbox
                        checked={Boolean(selected[index])}
                        onChange={(e) => {
                          handleSelect(e.target.checked, tableItem, index);
                        }}
                      />
                    </TableCell>
                    {headings.map((heading) => (
                      <TableCell>
                        {tableItem[heading.key as keyof typeof tableItem]}
                      </TableCell>
                    ))}
                    <TableCell>
                      {tableItem.skipVitals ? (
                        <CheckBoxIcon color="primary"/>
                      ) : (
                        <CheckBoxOutlineBlankIcon color="action" />
                      )}
                    </TableCell>
                    <TableCell>
                      {tableItem.isExternalRef ? (
                        <CheckBoxIcon color="primary" />
                      ) : (
                        <CheckBoxOutlineBlankIcon color="action" />
                      )}
                    </TableCell>
                    <TableCell>
                      {tableItem.isRefRequired ? (
                        <CheckBoxIcon color="primary" />
                      ) : (
                        <CheckBoxOutlineBlankIcon color="action" />
                      )}
                    </TableCell>
                    <TableCell>
                      {tableItem.deptStatus == 1 ? (
                        <div className="text-green-600 text-center rounded-[10px] w-[70px] font-semibold">
                          Active
                        </div>
                      ) : (
                        <div className="text-red-600 text-center rounded-[10px] w-[70px] font-semibold">
                          Inactive
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            handleNavigateClick(tableItem);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <div className="flex justify-center items-center gap-2">
                          <Switch
                            checked={Boolean(tableItem.deptStatus)}
                            disabled={
                              isLoading && tableItem.deptId == selectedId
                            }
                            onChange={(e) => {
                              handleUpdateOneStatus(
                                e.target.checked,
                                tableItem
                              );
                            }}
                          />
                          {isLoading && tableItem.deptId == selectedId && (
                            <CircularProgress size={18} />
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </CustomTableBody>
            </Table>
            <div className="flex justify-end">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component={"div"}
                count={tableData?.response?.totalCount || 0}
                rowsPerPage={searchParams.pageSize}
                page={searchParams.pageNumber}
                sx={{ border: 0 }}
                slotProps={{
                  select: {
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  },
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                // ActionsComponent={TablePaginationActions}
              />
            </div>
          </TableContainer>
        </div>
      </Paper>
    </div>
  );
};

export default SpecialityMaster;

const headings = [
  {
    label: "Speciality code",
    key: "deptCode",
  },
  {
    label: "Name",
    key: "deptName",
  },
];
const statusOptions = [
  { label: "All", value: 2 },
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];
const status = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];
const selectedStatus = {
  2: { label: "All", value: 2 },
  1: { label: "Active", value: 1 },
  0: { label: "Inactive", value: 0 },
};
